import { httpsCallable } from 'firebase/functions';
import { useCallback, useState } from 'react';
import type { PhotoBlurRegion } from '@citizen-walk/shared-types';
import { functions } from '../../lib/firebase';

/**
 * 写真のぼかしの管理者操作 (docs/28 §10-1、callable `photoPrivacyAdmin`)。
 * 処理 (ぼかしの作り直し) はサーバーで行い、結果は投稿の onSnapshot で画面に反映される。
 */
export type PhotoPrivacyAdminAction =
  | 'viewOriginal'
  | 'retry'
  | 'redetect'
  | 'applyRegions'
  | 'publishUnblurred'
  | 'withhold'
  | 'apply';

export interface PhotoPrivacyAdminResult {
  ok: true;
  url?: string;
  outcome?: string;
  message?: string;
}

export function usePhotoPrivacyAdmin(districtId: string, postId: string) {
  const [busy, setBusy] = useState<PhotoPrivacyAdminAction | null>(null);
  const run = useCallback(
    async (
      action: PhotoPrivacyAdminAction,
      regions?: PhotoBlurRegion[],
    ): Promise<PhotoPrivacyAdminResult> => {
      setBusy(action);
      try {
        const fn = httpsCallable<
          {
            districtId: string;
            postId: string;
            action: PhotoPrivacyAdminAction;
            regions?: PhotoBlurRegion[];
          },
          PhotoPrivacyAdminResult
        >(functions, 'photoPrivacyAdmin', { timeout: 300_000 });
        const res = await fn({ districtId, postId, action, ...(regions ? { regions } : {}) });
        return res.data;
      } finally {
        setBusy(null);
      }
    },
    [districtId, postId],
  );
  return { busy, run };
}
